import React, { useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useMediaQuery } from 'react-responsive';

import InsertHtmlContent from 'components/Common/InsertHtmlContent/InsertHtmlContent';
import MediaServer from 'components/Common/MediaServer/MediaServer';
import Trans from 'components/Common/Translation/Trans';
import AppliedFilters from 'components/Listing/AppliedFilters/AppliedFilters';
import Categories from 'components/Listing/Categories/Categories';
import CategoryProduct from 'components/Listing/CategoryProducts/CategoryProduct/CategoryProduct';
import FiltersDesktop from 'components/Listing/Filters/FiltersDesktop/FiltersDesktop';
import FiltersMobile from 'components/Listing/Filters/FiltersMobile/FiltersMobile';
import Pager from 'components/Listing/Pager/Pager';
import ProductListWithBigProducts from 'components/Listing/ProductList/ProductListWithBigProducts/ProductListWithBigProducts';
import ProductListWithSlots from 'components/Listing/ProductList/ProductListWithSlots/ProductListWithSlots';
import ProductsViewed from 'components/Listing/ProductsViewed/ProductsViewed';
import StickyWrapper from 'components/Listing/Common/StickyWrapper/StickyWrapper';
import Sorting from 'components/Listing/Sorting/Sorting';
import TextHeader from 'components/Listing/TextHeader/TextHeader';
import { BaseProps } from 'components/Listing/CategoryRenderTypes';
import useGtmPageView from 'hooks/gtm/useGtmPageView';
import useFilters from 'hooks/listing/useFilters';
import {
  pushFilterChangeEvent,
  pushProductClickEvent,
  pushProductImpressionEvent,
} from 'services/listing/gtm';
import { getUri } from 'services/generic';
import { formatListingProducts } from 'services/gtm/inputDataFormatter';
import { shouldForceSkuTargeting } from 'services/listing/listing';
import { trim } from 'services/string';
import { isBot } from 'services/ua';
import { ENV_HTML_SITEMAP_RELATIONSHIPS_CATEGORY_URL } from 'settings/env';
import { LARGE_LAPTOP_AND_DESKTOP } from 'settings/mediaQuery';
import { GTM_PAGE_LIST } from 'settings/gtm/gtmPageView';
import {
  TEMPLATE_BIG_PRODUCTS,
  TEMPLATE_DEFAULT,
  TEMPLATE_LARGE_SIZE,
} from 'settings/template';
import {
  AppliedFilterType,
  BreadcrumbType,
  DefaultProductListingType,
  FilterType,
  FilterValueType,
  ProductType,
} from 'types/ProductListing/ProductListing';

import styles from './CategoryProducts.module.scss';

export default function CategoryProducts({
  data,
  isDataValidating,
}: BaseProps<DefaultProductListingType>): JSX.Element {
  const { pathname, state, search, hash } = useLocation();
  const navigate = useNavigate();
  const isDesktop = useMediaQuery({ query: LARGE_LAPTOP_AND_DESKTOP });
  const productsWrapperRef = useRef<HTMLDivElement | null>(null);
  const {
    banner,
    bigProducts,
    blog,
    bottomBanner,
    breadcrumb,
    categories,
    filters,
    middleBanners,
    pager,
    products,
    topProducts,
    productCount,
    seo: { textFooter, textTop, title, titleH1 },
    slots,
    sorting,
    template,
  } = data;
  const { applied: appliedFilters, list: filtersList } = useFilters(filters);
  const allProducts = useMemo(
    () => [...topProducts, ...products, ...Object.values(bigProducts)],
    [topProducts, products, bigProducts]
  );
  const initialUrl = breadcrumb[breadcrumb.length - 1].url;
  const displayPager = Object.keys(pager.pages).length > 1;
  const preferCurvy = template === TEMPLATE_LARGE_SIZE;
  const canForceSkuTargeting = useMemo(
    () => shouldForceSkuTargeting(filtersList, appliedFilters),
    [filtersList, appliedFilters]
  );

  useEffect(() => {
    if (allProducts.length) pushProductImpressionEvent(allProducts.slice(0, 6));
  }, [allProducts]);

  useEffect(() => {
    if (state?.scrollToRef === true) {
      setTimeout(() => {
        if (productsWrapperRef.current) {
          productsWrapperRef.current.scrollIntoView({ behavior: 'auto' });
          // eslint-disable-next-line
          const { scroll, scrollToRef, ...rest } = state;
          navigate(pathname, {
            replace: true,
            state: rest,
          });
        }
      }, 100);
    }
  }, [pathname]);

  function handleFilterClick(filter: FilterType, filterValue: FilterValueType) {
    pushFilterChangeEvent(filter.label, filterValue.label);
  }

  function removeScrollToProductState() {
    if (state) {
      // eslint-disable-next-line
      const { scrollToProduct, ...rest } = state;
      navigate(pathname, {
        replace: true,
        state: rest,
      });
    }
  }

  function renderProduct(
    product: ProductType,
    className?: string,
    imageRatio?: string
  ): JSX.Element {
    const { id } = product;
    return (
      <CategoryProduct
        key={id}
        product={product}
        onClick={() => {
          pushProductClickEvent(product, appliedFilters);
          navigate(`${pathname}${search}${hash}`, {
            replace: true,
            state: { ...state, scrollToProduct: id },
          });
        }}
        scrollInto={state?.scrollToProduct === id}
        onScroll={removeScrollToProductState}
        forceSkuTargeting={canForceSkuTargeting}
        preferCurvy={preferCurvy}
        className={className}
        imageRatio={imageRatio}
      />
    );
  }

  return (
    <>
      <GtmPageView
        products={allProducts}
        title={title}
        appliedFilters={appliedFilters}
        breadcrumb={breadcrumb}
        currentSorting={sorting?.currentSorting}
        isDataValidating={isDataValidating}
      />
      {banner && (
        <InsertHtmlContent className={styles.topBanner} htmlContent={banner} />
      )}
      <h1 className={styles.title}>{titleH1}</h1>
      {textTop && (
        <TextHeader htmlContent={textTop} className={styles.textTop} />
      )}
      {categories && (
        <div className={styles.categoriesWrapper}>
          <Categories categories={categories} className={styles.categories} />
        </div>
      )}
      {isDesktop && filtersList.length && (
        <StickyWrapper className={styles.stickyWrapper}>
          {isSticky => (
            <FiltersDesktop
              filters={filtersList}
              onFilterClick={handleFilterClick}
              isSticky={isSticky}
              className={styles.filters}
            />
          )}
        </StickyWrapper>
      )}
      {isDesktop && !!appliedFilters.length && (
        <AppliedFilters
          filters={appliedFilters}
          resetUrl={getUri(initialUrl)}
          className={styles.appliedFilters}
        />
      )}
      <div className={styles.actionBar}>
        <span className={styles.nbProducts}>
          <Trans
            i18nKey={'NB_PRODUCTS'}
            namespace={'listingPage'}
            values={{ count: productCount.toString() }}
          />
        </span>
        {sorting && <Sorting sorting={sorting.list} />}
        {!isDesktop && filtersList.length && (
          <FiltersMobile
            filters={filtersList}
            resetUrl={getUri(initialUrl)}
            productCount={productCount}
            onFilterClick={handleFilterClick}
            classes={{ toggleButton: styles.filterToggleButton }}
          />
        )}
      </div>
      <div ref={productsWrapperRef} className={styles.productListWrapper}>
        {[TEMPLATE_DEFAULT, TEMPLATE_LARGE_SIZE].includes(template) && (
          <ProductListWithSlots
            products={products}
            topProducts={topProducts}
            renderProduct={renderProduct}
            slots={slots}
            middleBanners={middleBanners}
            className={styles.productListContainer}
          />
        )}
        {template === TEMPLATE_BIG_PRODUCTS && (
          <ProductListWithBigProducts
            products={products}
            topProducts={topProducts}
            bigProducts={bigProducts}
            renderProduct={renderProduct}
            middleBanners={middleBanners}
            className={styles.productListContainer}
          />
        )}
      </div>
      <ProductsViewed
        count={pager.currentProductCount}
        total={productCount}
        className={styles.pagerProgression}
      />
      {displayPager && (
        <Pager pager={pager} classes={{ container: styles.pagerContainer }} />
      )}
      {blog && <InsertHtmlContent htmlContent={blog} className={styles.blog} />}
      {textFooter && (
        <InsertHtmlContent
          htmlContent={textFooter}
          className={styles.textFooter}
        />
      )}
      {bottomBanner && (
        <InsertHtmlContent
          htmlContent={bottomBanner}
          className={styles.bottomBanner}
        />
      )}
      {(!isBot() || pager.currentPageNumber === 1) &&
        initialUrl &&
        ENV_HTML_SITEMAP_RELATIONSHIPS_CATEGORY_URL && (
          <div className={styles.sitemapWrapper}>
            <MediaServer
              path={ENV_HTML_SITEMAP_RELATIONSHIPS_CATEGORY_URL.replace(
                '%identifier%',
                trim(initialUrl, '/')
              )}
            />
          </div>
        )}
    </>
  );
}

type GtmPageViewProps = {
  title: string;
  appliedFilters: AppliedFilterType[];
  breadcrumb: BreadcrumbType[];
  currentSorting?: string;
  products: ProductType[];
  isDataValidating: boolean;
};

function GtmPageView({
  title,
  appliedFilters,
  breadcrumb,
  currentSorting,
  products,
  isDataValidating,
}: GtmPageViewProps) {
  useGtmPageView(GTM_PAGE_LIST, title, {
    appliedFilters,
    breadcrumb,
    currentSortingGroup: currentSorting,
    products: formatListingProducts(products),
    isLoaded: !isDataValidating,
  });

  return null;
}
